// @ts-nocheck
import { useState, useEffect, useCallback, useRef } from 'react';
import collaborationWebSocketService from '../services/collaborationWebSocketService';
import type { ParticipantInfo } from '../services/collaborationWebSocketService';
import { useAuth } from './useAuth';

export interface CollaborationParticipant {
  id: string;
  name: string;
  role: string;
  color: string;
  isOnline: boolean;
  lastSeen: Date;
  permissions: string[];
}

export interface CollaborationState {
  sessionId: string | null;
  participants: ParticipantInfo[];
  isConnected: boolean;
  isReconnecting: boolean;
  connectionError: Error | null;
  cursors: Map<string, { x: number; y: number; color: string }>;
  selections: Map<string, { start: number; end: number; color: string }>;
  annotations: any[];
  edits: any[];
  conflicts: any[];
  sessionMetadata: any;
  capabilities: {
    canEdit: boolean;
    canAnnotate: boolean;
    canVoiceChat: boolean;
    canScreenShare: boolean;
    canModerate: boolean;
  };
}

export interface UseCollaborationOptions {
  sessionId?: string;
  autoConnect?: boolean;
  reconnectAttempts?: number;
  reconnectDelay?: number;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
  onParticipantJoin?: (participant: CollaborationParticipant) => void;
  onParticipantLeave?: (participantId: string) => void;
  onEdit?: (edit: any) => void;
  onConflict?: (conflict: any) => void;
  onAnnotation?: (annotation: any) => void;
  onCursorMove?: (cursor: any) => void;
  onSelectionShare?: (selection: any) => void;
}

export const useCollaboration = (options: UseCollaborationOptions = {}) => {
  const { user } = useAuth();
  const wsServiceRef = useRef<typeof collaborationWebSocketService | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);

  const [state, setState] = useState<CollaborationState>({
    sessionId: options.sessionId || null,
    participants: [],
    isConnected: false,
    isReconnecting: false,
    connectionError: null,
    cursors: new Map(),
    selections: new Map(),
    annotations: [],
    edits: [],
    conflicts: [],
    sessionMetadata: null,
    capabilities: {
      canEdit: false,
      canAnnotate: false,
      canVoiceChat: false,
      canScreenShare: false,
      canModerate: false,
    },
  });

  // Initialize WebSocket service
  useEffect(() => {
    if (!options.sessionId || !user) return;

    const initializeWebSocket = async () => {
      try {
        setState(prev => ({ ...prev, isReconnecting: true, connectionError: null }));

        // Get WebSocket token from API
        const authToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
        const response = await fetch(`/api/v1/collaboration/sessions/${options.sessionId}/token`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to get collaboration token');
        }

        const { token, url } = await response.json();

        // Create WebSocket service
        wsServiceRef.current = collaborationWebSocketService;
        await wsServiceRef.current.connect(url, user?.user_id || '', user?.email || 'Anonymous');

        // Set up event listeners
        wsServiceRef.current.on('connect', handleConnect);
        wsServiceRef.current.on('disconnect', handleDisconnect);
        wsServiceRef.current.on('error', handleError);
        wsServiceRef.current.on('participantJoined', handleParticipantJoin);
        wsServiceRef.current.on('participantLeft', handleParticipantLeave);
        wsServiceRef.current.on('edit', handleEdit);
        wsServiceRef.current.on('conflict', handleConflict);
        wsServiceRef.current.on('annotation', handleAnnotation);
        wsServiceRef.current.on('cursorMove', handleCursorMove);
        wsServiceRef.current.on('selectionShare', handleSelectionShare);
        wsServiceRef.current.on('sessionUpdate', handleSessionUpdate);

        // Connect
        await wsServiceRef.current.connect();
      } catch (error) {
        console.error('Failed to initialize WebSocket:', error);
        setState(prev => ({
          ...prev,
          isReconnecting: false,
          connectionError: error as Error,
        }));
        handleReconnect();
      }
    };

    if (options.autoConnect !== false) {
      initializeWebSocket();
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = undefined;
      }
      if (wsServiceRef.current) {
        // Remove all event listeners before disconnecting
        wsServiceRef.current.removeAllListeners();
        wsServiceRef.current.disconnect();
        wsServiceRef.current = null;
      }
    };
  }, [options.sessionId, user]);

  // Handle connection events
  const handleConnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    setState(prev => ({
      ...prev,
      isConnected: true,
      isReconnecting: false,
      connectionError: null,
    }));
    options.onConnect?.();
  }, [options]);

  const handleDisconnect = useCallback(() => {
    setState(prev => ({
      ...prev,
      isConnected: false,
    }));
    options.onDisconnect?.();
    handleReconnect();
  }, [options]);

  const handleError = useCallback(
    (error: Error) => {
      setState(prev => ({
        ...prev,
        connectionError: error,
      }));
      options.onError?.(error);
    },
    [options]
  );

  const handleReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= (options.reconnectAttempts || 5)) {
      console.error('Max reconnection attempts reached');
      return;
    }

    reconnectAttemptsRef.current++;
    const delay = (options.reconnectDelay || 1000) * Math.min(reconnectAttemptsRef.current, 5);

    setState(prev => ({ ...prev, isReconnecting: true }));

    reconnectTimeoutRef.current = setTimeout(() => {
      if (wsServiceRef.current) {
        wsServiceRef.current.connect().catch(console.error);
      }
    }, delay);
  }, [options]);

  // Handle collaboration events
  const handleParticipantJoin = useCallback(
    (participant: CollaborationParticipant) => {
      setState(prev => ({
        ...prev,
        participants: [...prev.participants, participant],
      }));
      options.onParticipantJoin?.(participant);
    },
    [options]
  );

  const handleParticipantLeave = useCallback(
    (participantId: string) => {
      setState(prev => ({
        ...prev,
        participants: prev.participants.filter(p => p.id !== participantId),
        cursors: new Map(Array.from(prev.cursors.entries()).filter(([id]) => id !== participantId)),
        selections: new Map(Array.from(prev.selections.entries()).filter(([id]) => id !== participantId)),
      }));
      options.onParticipantLeave?.(participantId);
    },
    [options]
  );

  const handleEdit = useCallback(
    (edit: any) => {
      setState(prev => ({
        ...prev,
        edits: [...prev.edits, edit],
      }));
      options.onEdit?.(edit);
    },
    [options]
  );

  const handleConflict = useCallback(
    (conflict: any) => {
      setState(prev => ({
        ...prev,
        conflicts: [...prev.conflicts, conflict],
      }));
      options.onConflict?.(conflict);
    },
    [options]
  );

  const handleAnnotation = useCallback(
    (annotation: any) => {
      setState(prev => ({
        ...prev,
        annotations: [...prev.annotations, annotation],
      }));
      options.onAnnotation?.(annotation);
    },
    [options]
  );

  const handleCursorMove = useCallback(
    (cursor: any) => {
      setState(prev => ({
        ...prev,
        cursors: new Map(prev.cursors).set(cursor.participantId, {
          x: cursor.x,
          y: cursor.y,
          color: cursor.color,
        }),
      }));
      options.onCursorMove?.(cursor);
    },
    [options]
  );

  const handleSelectionShare = useCallback(
    (selection: any) => {
      setState(prev => ({
        ...prev,
        selections: new Map(prev.selections).set(selection.participantId, {
          start: selection.start,
          end: selection.end,
          color: selection.color,
        }),
      }));
      options.onSelectionShare?.(selection);
    },
    [options]
  );

  const handleSessionUpdate = useCallback((update: any) => {
    setState(prev => ({
      ...prev,
      sessionMetadata: update.metadata,
      participants: update.participants || prev.participants,
      capabilities: update.capabilities || prev.capabilities,
    }));
  }, []);

  // Public methods
  const sendEdit = useCallback(
    (content: string, position?: number) => {
      if (!wsServiceRef.current || !state.isConnected) {
        console.warn('Cannot send edit: WebSocket not connected');
        return;
      }
      wsServiceRef.current.sendEdit(content, position);
    },
    [state.isConnected]
  );

  const sendCursorPosition = useCallback(
    (x: number, y: number) => {
      if (!wsServiceRef.current || !state.isConnected) return;
      wsServiceRef.current.sendCursorPosition(x, y);
    },
    [state.isConnected]
  );

  const shareSelection = useCallback(
    (start: number, end: number, text?: string) => {
      if (!wsServiceRef.current || !state.isConnected) return;
      wsServiceRef.current.shareSelection(start, end, text);
    },
    [state.isConnected]
  );

  const createAnnotation = useCallback(
    (
      type: 'note' | 'highlight' | 'question' | 'suggestion',
      content: string,
      position?: { start: number; end: number }
    ) => {
      if (!wsServiceRef.current || !state.isConnected) return;
      wsServiceRef.current.createAnnotation(type, content, position);
    },
    [state.isConnected]
  );

  const resolveConflict = useCallback(
    (conflictId: string, resolution: 'accept' | 'reject' | 'merge', mergedContent?: string) => {
      if (!wsServiceRef.current || !state.isConnected) return;
      wsServiceRef.current.resolveConflict(conflictId, resolution, mergedContent);
    },
    [state.isConnected]
  );

  const startVoiceChat = useCallback(() => {
    if (!wsServiceRef.current || !state.isConnected) return;
    wsServiceRef.current.startVoiceChat();
  }, [state.isConnected]);

  const endVoiceChat = useCallback(() => {
    if (!wsServiceRef.current || !state.isConnected) return;
    wsServiceRef.current.endVoiceChat();
  }, [state.isConnected]);

  const connect = useCallback(() => {
    if (!wsServiceRef.current) return;
    wsServiceRef.current.connect();
  }, []);

  const disconnect = useCallback(() => {
    if (!wsServiceRef.current) return;
    wsServiceRef.current.disconnect();
  }, []);

  return {
    ...state,
    sendEdit,
    sendCursorPosition,
    shareSelection,
    createAnnotation,
    resolveConflict,
    startVoiceChat,
    endVoiceChat,
    connect,
    disconnect,
  };
};

export default useCollaboration;
